import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Play, X } from "@phosphor-icons/react";
import Brand from "../../components/Brand/Brand";
import { SHOWREEL, useContent } from "./content";
import { useHomeLang } from "./homeLang";
import "./proof.css";

// The section's own words; the videos' names are built round the advisor's
// name, `who`, and the video's length, `duration`.
const WORDS = {
  it: {
    heading: "Questo video è stato realizzato in pochi minuti con A.V.R.A.H.",
    reel: "Video di presentazione di AVRAH, girato con AVRAH",
    voices: "Testimonianze",
    press: "Dicono di noi",
    read: "Leggi l’articolo",
    watch: (label, duration) => `Guarda: ${label}, ${duration}`,
    watchVoice: "Guarda il video",
    watchVoiceLabel: (who, duration) => `Guarda il video di ${who}, ${duration}`,
    close: "Chiudi",
    voiceLabel: (who) => `Testimonianza video di ${who}`,
  },
  en: {
    heading: "This video was made in just a few minutes with A.V.R.A.H.",
    reel: "AVRAH introduction video, filmed with AVRAH",
    voices: "Testimonials",
    press: "In the press",
    read: "Read the article",
    watch: (label, duration) => `Watch: ${label}, ${duration}`,
    watchVoice: "Watch the video",
    watchVoiceLabel: (who, duration) => `Watch the video from ${who}, ${duration}`,
    close: "Close",
    voiceLabel: (who) => `Video testimonial from ${who}`,
  },
};

// The proof, in one place: the showreel made with AVRAH, what the advisors
// who use it say, by name, and, quieter and set apart in form, what the press
// wrote. The advisors' words come first; their faces only in their videos,
// which open over the whole window. A video loads only when asked for, and
// only one plays at a time.
export default function ProofSection() {
  const { PRESS, TESTIMONIALS } = useContent();
  const words = WORDS[useHomeLang()];
  const [playing, setPlaying] = useState(null);
  const player = (video) => ({
    video,
    playing: playing === video.src,
    onPlay: () => setPlaying(video.src),
    onEnd: () => setPlaying(null),
  });

  return (
    <section aria-labelledby="hf-h-proof" className="hf-sec hf-wrap">
      <h2 id="hf-h-proof" className="t-h2 pf-h2"><Brand>{words.heading}</Brand></h2>
      <ClickToPlay className="pf-reel" label={words.reel} words={words} {...player(SHOWREEL)} />

      <h3 className="pf-group">{words.voices}</h3>
      <ul className="pf-voices">
        {TESTIMONIALS.map((t) => <Voice key={t.src} words={words} {...player(t)} />)}
      </ul>
      <Theatre words={words} voice={TESTIMONIALS.find((t) => t.src === playing)} onClose={() => setPlaying(null)} />

      <div className="pf-press">
        <h3 className="pf-press-h">{words.press}</h3>
        <ul className="pf-articles">
          {PRESS.map((p) => (
            <li key={p.href}>
              <a className="pf-article" href={p.href} target="_blank" rel="noopener noreferrer">
                <span className="pf-from"><strong>{p.outlet}</strong>, {p.when}</span>
                <span className="pf-headline">{p.title}</span>
                <span className="btn btn-plain btn-plain-start pf-more"><ArrowUpRight size={18} weight="bold" aria-hidden="true" />{words.read}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// A poster with play and its length over it, that becomes the playing video
// when clicked.
function ClickToPlay({ className, label, words, video, playing, onPlay, onEnd }) {
  if (playing) {
    return <video className={className} src={video.src} poster={video.poster} autoPlay controls playsInline onEnded={onEnd} aria-label={label} />;
  }
  return (
    <button type="button" className={`${className} pf-cover`} onClick={onPlay} aria-label={words.watch(label, video.duration)}>
      <img src={video.poster} alt="" loading="lazy" />
      <span className="pf-play" aria-hidden="true"><Play weight="fill" />{video.duration}</span>
    </button>
  );
}

// An advisor's words, large, signed with their name, and the button that
// opens their video in the theatre.
function Voice({ video: t, words, onPlay }) {
  return (
    <li className="pf-voice">
      <blockquote className="pf-quote">«{t.quote}»</blockquote>
      <div className="pf-sign">
        <p className="pf-who">{t.who}</p>
        <button type="button" className="btn btn-plain btn-plain-start" onClick={onPlay} aria-label={words.watchVoiceLabel(t.who, t.duration)}>
          <Play weight="fill" />{words.watchVoice}<span className="pf-dur">{t.duration}</span>
        </button>
      </div>
    </li>
  );
}

// The `voice` playing over the whole window, as large as the window lets it
// be, signed with its name, until it ends or is closed. The browser keeps
// focus in it, closes it on Escape and gives focus back to the button that
// opened it.
function Theatre({ voice, words, onClose }) {
  const theatre = useRef(null);
  const closer = useRef(null);
  useEffect(() => {
    if (voice && !theatre.current.open) {
      theatre.current.showModal();
      closer.current.focus();
    }
  }, [voice]);
  const close = () => theatre.current.close();

  return (
    <dialog ref={theatre} className="pf-theatre" aria-labelledby="pf-theatre-who" onClose={onClose}>
      {voice && (
        <>
          <div className="pf-theatre-bar hf-wrap">
            <button ref={closer} type="button" className="btn btn-quiet" onClick={close}><X aria-hidden="true" />{words.close}</button>
          </div>
          <div className="pf-theatre-stage hf-wrap">
            <figure className="pf-theatre-shot">
              <video src={voice.src} poster={voice.poster} autoPlay controls playsInline onEnded={close} aria-label={words.voiceLabel(voice.who)} />
              <figcaption id="pf-theatre-who" className="pf-who">{voice.who}</figcaption>
            </figure>
          </div>
        </>
      )}
    </dialog>
  );
}
